<script setup>
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import { useHomeStore } from "@/stores/HomeStore";

const frequencies = [
  { value: "monthly", label: "Monthly", priceSuffix: "/month" },
  { value: "annually", label: "Annually", priceSuffix: "/year", discount: "Save 17%" },
];

const tiers = [
  {
    name: "Starter",
    id: "tier-starter",
    href: "#",
    featured: false,
    description: "Perfect for casual traders getting started",
    price: { monthly: "0", annually: "0" },
    features: [
      { name: "Manual transaction entry", included: true },
      { name: "Real-time PNL tracking", included: true },
      { name: "Basic portfolio summary", included: true },
      { name: "Up to 3 price alerts", included: true },
      { name: "Exchange integrations", included: false },
      { name: "Advanced reports & charts", included: false },
      { name: "Tax calculations", included: false },
      { name: "Priority support", included: false },
    ],
  },
  {
    name: "Pro",
    id: "tier-pro",
    href: "#",
    featured: true,
    description: "For serious traders who need advanced tools",
    price: { monthly: "10", annually: "100" },
    badge: "Most Popular",
    features: [
      { name: "Manual & automated transaction entry", included: true },
      { name: "Real-time PNL tracking", included: true },
      { name: "Advanced portfolio analytics", included: true },
      { name: "Unlimited price alerts", included: true },
      { name: "All exchange integrations", included: true },
      { name: "Advanced reports & charts", included: true },
      { name: "Tax calculations (Norway)", included: true },
      { name: "Priority support", included: true },
    ],
  },
  {
    name: "Enterprise",
    id: "tier-enterprise",
    href: "#",
    featured: false,
    description: "Custom solution for trading teams",
    price: { monthly: "Custom", annually: "Custom" },
    features: [
      { name: "Everything in Pro", included: true },
      { name: "Custom integrations", included: true },
      { name: "API access", included: true },
      { name: "Dedicated account manager", included: true },
      { name: "Custom reports", included: true },
      { name: "Advanced security features", included: true },
      { name: "SLA guarantee", included: true },
      { name: "White-label options", included: true },
    ],
  },
];

const frequency = ref(frequencies[0]);

const homeStore = useHomeStore();
const signUp = () => {
  homeStore.signIn = false;
};
</script>

<template>
  <div class="relative py-24 overflow-hidden bg-background sm:py-32" id="pricing">
    <!-- Background Pattern -->
    <div class="absolute inset-0 -z-10">
      <div
        class="absolute inset-y-0 right-1/2 -z-10 mr-16 w-[200%] origin-bottom-left skew-x-[-30deg] bg-muted/30 shadow-xl shadow-primary/5 ring-1 ring-border sm:mr-28 lg:mr-0 xl:mr-16 xl:origin-center" />
    </div>

    <div class="px-6 mx-auto max-w-7xl lg:px-8">
      <!-- Section Header -->
      <div class="max-w-4xl mx-auto text-center">
        <div class="inline-flex items-center px-3 py-1 mb-6 text-sm font-medium rounded-full bg-secondary/10 text-secondary">
          Pricing
        </div>
        <h2 class="text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl">
          Simple, transparent pricing
        </h2>
        <p class="max-w-2xl mx-auto mt-6 text-lg leading-8 text-muted-foreground">
          Choose the plan that fits your trading volume. Upgrade or downgrade anytime with no long-term commitments.
        </p>

        <!-- Frequency Toggle -->
        <div class="flex justify-center mt-10">
          <RadioGroup
            v-model="frequency"
            class="grid grid-cols-2 p-1.5 text-sm font-semibold leading-5 rounded-xl bg-card border border-border shadow-sm">
            <RadioGroupLabel class="sr-only">Payment frequency</RadioGroupLabel>
            <RadioGroupOption
              as="template"
              v-for="option in frequencies"
              :key="option.value"
              :value="option"
              v-slot="{ checked }">
              <div
                :class="[
                  checked
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                  'cursor-pointer rounded-lg px-6 py-2.5 transition-all duration-200 relative',
                ]">
                <span>{{ option.label }}</span>
                <span
                  v-if="option.discount"
                  class="absolute -top-3 -right-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-green-500 text-white">
                  {{ option.discount }}
                </span>
              </div>
            </RadioGroupOption>
          </RadioGroup>
        </div>
      </div>

      <!-- Pricing Cards -->
      <div class="grid max-w-6xl grid-cols-1 mx-auto mt-16 gap-8 lg:grid-cols-3">
        <div
          v-for="tier in tiers"
          :key="tier.id"
          :class="[
            tier.featured
              ? 'ring-2 ring-primary shadow-2xl scale-105 z-10'
              : 'ring-1 ring-border',
            'relative rounded-2xl bg-card p-8 transition-all duration-300 hover:shadow-xl',
          ]">
          <!-- Badge for Featured Plan -->
          <div
            v-if="tier.badge"
            class="absolute px-4 py-1 text-sm font-semibold -translate-x-1/2 rounded-full -top-4 left-1/2 bg-gradient-to-r from-primary to-secondary text-primary-foreground shadow-lg">
            {{ tier.badge }}
          </div>

          <!-- Plan Header -->
          <div class="flex items-center justify-between">
            <h3 :id="tier.id" class="text-2xl font-bold text-foreground">
              {{ tier.name }}
            </h3>
          </div>
          <p class="mt-2 text-sm text-muted-foreground">
            {{ tier.description }}
          </p>

          <!-- Price -->
          <div class="flex items-baseline mt-6 gap-x-2">
            <span
              class="text-5xl font-bold tracking-tight text-foreground">
              {{ typeof tier.price[frequency.value] === 'string' && tier.price[frequency.value].includes('$')
                ? tier.price[frequency.value]
                : `$${tier.price[frequency.value]}` }}
            </span>
            <span
              v-if="tier.price[frequency.value] !== 'Custom'"
              class="text-base font-medium text-muted-foreground">
              {{ frequency.priceSuffix }}
            </span>
          </div>

          <!-- CTA Button -->
          <a
            :href="tier.href"
            @click="signUp"
            :aria-describedby="tier.id"
            :class="[
              tier.featured
                ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md'
                : 'bg-card text-foreground border border-border hover:bg-accent',
              'mt-8 block w-full rounded-lg px-6 py-3.5 text-center text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            ]">
            {{ tier.price[frequency.value] === 'Custom' ? 'Contact Sales' : 'Get Started' }}
          </a>

          <!-- Features List -->
          <ul role="list" class="mt-10 space-y-4 text-sm leading-6">
            <li
              v-for="feature in tier.features"
              :key="feature.name"
              class="flex items-start gap-x-3">
              <UIcon
                :name="feature.included ? 'i-heroicons-check-circle' : 'i-heroicons-minus-circle'"
                :class="[
                  feature.included
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-muted-foreground opacity-40',
                  'h-5 w-5 flex-none mt-0.5',
                ]"
                aria-hidden="true" />
              <span
                :class="[
                  feature.included ? 'text-foreground' : 'text-muted-foreground',
                ]">
                {{ feature.name }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <!-- FAQ Callout -->
      <div class="max-w-4xl p-6 mx-auto mt-16 text-center rounded-xl bg-muted">
        <p class="text-sm font-medium text-muted-foreground">
          Have questions about pricing?
          <a
            href="#support"
            class="ml-2 font-semibold transition-colors duration-200 text-secondary hover:text-secondary/80">
            Contact our team →
          </a>
        </p>
      </div>
    </div>
  </div>
</template>
